import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { getItemsIncludingDeleted } from "../../firebase/itemsAndInventory/getItems";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Alert,
	Typography,
	IconButton,
	TextField,
	TableFooter,
	Chip,
	Link,
	Box,
	Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import { ItemInfo } from "../../models/item";
import {
	auditedDeleteItem,
	auditedUpdateItem
} from "../../firebase/auditLog/wrappedOperations";
import Barcode from "./Barcode";
import EditIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import EditItemDialog from "./EditItemDialog";
import { ItemAuditLogDialog } from "../AuditLog/ItemAuditLogDialog";
import { formatDate } from "../../utils/calculations";
import { useAuth } from "../../context/AuthContext";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { exportItemsListToExcel } from "../../utils/exportItemsListToExcel";
import { exportItemsListToPdf } from "../../utils/exportItemsListToPdf";
import { getPartnerInvestmentsByDateRange } from "../../firebase/partnerInvestment/getPartnerInvestmentsByDateRange";
import { getInvestmentCodeForItem } from "../../utils/partnerInvestmentLookup";
import {
	getInitialItemEditState,
	getItemDeleteValidationError,
	getItemEditStateForClose,
	getItemEditStateForOpen,
	updateItemsWithEditedItem
} from "../../utils/itemActionsFlow";
import { useNavigate } from "react-router-dom";

// This component shows all items, including soft deleted ones (with strikethrough for deleted)
export const ItemsListWithDeleted: React.FC = () => {
	const navigate = useNavigate();
	const [editState, setEditState] = useState(getInitialItemEditState);
	const [selectedAuditItem, setSelectedAuditItem] = useState<{
		itemName: string;
		itemCode: string;
	} | null>(null);
	const [isAuditDialogOpen, setIsAuditDialogOpen] = useState(false);

	const [items, setItems] = useState<{ [key: string]: ItemInfo }>({});
	const [investmentCodes, setInvestmentCodes] = useState<
		Record<string, string | null>
	>({});
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const { user } = useAuth();

	useEffect(() => {
		const fetchItems = async () => {
			try {
				const [fetchedItems, investments] = await Promise.all([
					getItemsIncludingDeleted(),
					getPartnerInvestmentsByDateRange(
						"2000-01-01",
						new Date().toLocaleDateString("en-CA")
					)
				]);
				// Sort items by createdAt descending
				const sortedItems = Object.fromEntries(
					Object.entries(fetchedItems).sort(
						(a, b) =>
							new Date(b[1].createdAt).getTime() -
							new Date(a[1].createdAt).getTime()
					)
				);
				const codeMap = Object.keys(sortedItems).reduce<
					Record<string, string | null>
				>((acc, key) => {
					acc[key] = getInvestmentCodeForItem(
						sortedItems[key],
						investments
					);
					return acc;
				}, {});
				setItems(sortedItems);
				setInvestmentCodes(codeMap);
			} catch (err) {
				setError("Failed to fetch items. Please try again later.");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		void fetchItems();
	}, []);

	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	/**
	 * Delete Item (Soft Delete)
	 * @param itemId : key of item
	 * @returns
	 */
	const handleDelete = async (itemId: string) => {
		const item = items[itemId];
		if (!item) return;
		const validationError = getItemDeleteValidationError(item);
		if (validationError) {
			if (validationError === "Item not found.") {
				return;
			}
			alert(validationError);
			return;
		}
		if (window.confirm("Are you sure you want to delete this item?")) {
			try {
				await auditedDeleteItem(itemId);
				const newItems = { ...items };
				if (newItems[itemId]) {
					newItems[itemId].isDeleted = true;
				}
				setItems(newItems);
			} catch (err) {
				setError("Failed to delete item. Please try again later.");
				console.error(err);
			}
		}
	};

	const handleEdit = (itemId: string, item: ItemInfo) => {
		setEditState(getItemEditStateForOpen(itemId, item));
	};

	const handleEditDialogClose = () => {
		setEditState(getItemEditStateForClose());
	};

	const handleEditSave = async (updatedItem: ItemInfo) => {
		if (!editState.editItemId) return;
		try {
			await auditedUpdateItem(editState.editItemId, updatedItem);
			setItems((prev) =>
				updateItemsWithEditedItem(
					prev,
					editState.editItemId!,
					updatedItem
				)
			);
			handleEditDialogClose();
		} catch (err) {
			setError("Failed to update item. Please try again later.");
			console.error(err);
		}
	};

	const handlePrintBarcode = (item: ItemInfo, itemKey: string) => {
		const printWindow = window.open("", "_blank");
		if (printWindow) {
			const printContent = document.createElement("div");
			printWindow.document.body.appendChild(printContent);

			const root = createRoot(printContent);
			root.render(
				<React.StrictMode>
					<Barcode itemKey={itemKey} itemInfo={item} />
					<IconButton
						aria-label="print barcode"
						onClick={() => printWindow.print()}
					>
						Print
					</IconButton>
				</React.StrictMode>
			);
		}
	};

	const openAuditLogsForItem = (itemName: string, itemCode: string) => {
		setSelectedAuditItem({ itemName, itemCode });
		setIsAuditDialogOpen(true);
	};

	const closeAuditDialog = () => {
		setIsAuditDialogOpen(false);
		setSelectedAuditItem(null);
	};

	if (loading) {
		return (
			<Paper
				elevation={3}
				sx={{
					p: 2,
					display: "flex",
					justifyContent: "center",
					alignItems: "center"
				}}
			>
				<CircularProgress />
			</Paper>
		);
	}

	if (error) {
		return <Alert severity="error">{error}</Alert>;
	}

	// Lets match all words in the search term
	const wordsToMatch: string[] = searchTerm
		.split(" ")
		.map((word) => word.trim().toLowerCase());

	const filteredItems = Object.entries(items).filter(([key, item]) =>
		wordsToMatch.every(
			(word) =>
				item.name.toLowerCase().includes(word) ||
				item.itemCode.toLowerCase().includes(word)
		)
	);

	const totalQuantity = filteredItems.reduce((acc, [, item]) => {
		return acc + (item.availableQuantity || 0);
	}, 0);

	const totalPurchaseValue = filteredItems.reduce((acc, [, item]) => {
		return acc + item.purchasePrice * (item.availableQuantity || 0);
	}, 0);

	const handleExportItems = async () => {
		try {
			await exportItemsListToExcel(Object.fromEntries(filteredItems));
		} catch (err) {
			setError("Failed to export items. Please try again later.");
			console.error(err);
		}
	};

	const handleExportItemsPdf = () => {
		try {
			exportItemsListToPdf(Object.fromEntries(filteredItems));
		} catch (err) {
			setError("Failed to export items PDF. Please try again later.");
			console.error(err);
		}
	};

	return (
		<>
			<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2
					}}
				>
					<Typography variant="h5">Inventory: All Items</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Tooltip title="Go to Home">
							<IconButton
								color="primary"
								aria-label="go to home"
								onClick={() => navigate("/")}
							>
								<HomeIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Add inventory item">
							<IconButton
								color="primary"
								aria-label="add inventory item"
								onClick={() => navigate("/add-item")}
							>
								<AddIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Export items list to Excel">
							<IconButton
								color="primary"
								aria-label="export items list to excel"
								onClick={handleExportItems}
							>
								<DownloadIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Export items list to PDF">
							<IconButton
								color="secondary"
								aria-label="export items list to pdf"
								onClick={handleExportItemsPdf}
							>
								<PictureAsPdfIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
				<TextField
					label="Filter by item name or code"
					variant="outlined"
					fullWidth
					margin="normal"
					onChange={(e) => setSearchTerm(e.target.value)}
				/>
				<TableContainer component={Paper}>
					<Table
						sx={{
							"& .MuiTableCell-root": {
								border: "1px solid rgba(224, 224, 224, 1)"
							},
							"& .MuiTableCell-head": {
								fontWeight: "bold"
							}
						}}
					>
						<TableHead>
							<TableRow>
								<TableCell>Item Name</TableCell>
								<TableCell>Investment Code</TableCell>
								<TableCell>Date</TableCell>
								<TableCell>Purchase Price</TableCell>
								<TableCell>Sell Price</TableCell>
								<TableCell>Minimum Profit %</TableCell>
								<TableCell>Avl. Qty.</TableCell>
								<TableCell>Item Code</TableCell>
								{user?.email === "madhv.net@gmail.com" && (
									<TableCell>Actions</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredItems.map(([key, item]) => {
								const isDeleted = item.isDeleted;
								return (
									<TableRow
										key={key}
										sx={
											isDeleted
												? {
														backgroundColor:
															"#f5f5f5",
														"& td": {
															textDecoration:
																"line-through",
															color: "#888"
														}
													}
												: {}
										}
									>
										<TableCell>
											<Link
												component="button"
												onClick={() =>
													openAuditLogsForItem(
														item.name,
														item.itemCode
													)
												}
												underline="hover"
											>
												{item.name}
											</Link>
										</TableCell>
										<TableCell>
											{investmentCodes[key] ? (
												<Chip
													size="small"
													label={investmentCodes[key]}
													color="warning"
												/>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell>
											{formatDate(item.createdAt)}
										</TableCell>
										<TableCell>{`₹${item.purchasePrice.toFixed(
											2
										)}`}</TableCell>
										<TableCell>{`₹${item.sellPrice.toFixed(
											2
										)}`}</TableCell>
										<TableCell>
											{item.minProfitPercent.toFixed(2)}
										</TableCell>
										<TableCell>
											{item.availableQuantity?.toFixed(0)}
										</TableCell>
										<TableCell>
											{item.itemCode}
											<IconButton
												aria-label="copy item code"
												onClick={() =>
													handleCopy(item.itemCode)
												}
												size="small"
												disabled={isDeleted}
											>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
										</TableCell>
										{user?.email ===
											"madhv.net@gmail.com" && (
											<TableCell>
												<IconButton
													aria-label="edit"
													onClick={() =>
														handleEdit(key, item)
													}
													disabled={isDeleted}
												>
													<EditIcon />
												</IconButton>
												<IconButton
													aria-label="delete"
													onClick={() =>
														handleDelete(key)
													}
													disabled={isDeleted}
												>
													<DeleteIcon />
												</IconButton>
												<IconButton
													aria-label="print barcode"
													onClick={() =>
														handlePrintBarcode(
															item,
															key
														)
													}
													disabled={isDeleted}
												>
													<PrintIcon />
												</IconButton>
											</TableCell>
										)}
									</TableRow>
								);
							})}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TableCell colSpan={2} align="right">
									<Typography
										variant="h6"
										sx={{ color: "purple" }}
									>
										Total Purchase Value:
									</Typography>
								</TableCell>
								<TableCell>
									<Typography
										variant="h6"
										sx={{ color: "purple" }}
									>
										{`₹${totalPurchaseValue.toFixed(2)}`}
									</Typography>
								</TableCell>
								<TableCell colSpan={2} align="right">
									<Typography
										variant="h6"
										sx={{ color: "green" }}
									>
										Total Available Quantity:
									</Typography>
								</TableCell>
								<TableCell>
									<Typography
										variant="h6"
										sx={{ color: "green" }}
									>
										{totalQuantity.toFixed(0)}
									</Typography>
								</TableCell>
								<TableCell
									colSpan={
										user?.email === "madhv.net@gmail.com"
											? 2
											: 1
									}
								></TableCell>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			</Paper>
			<EditItemDialog
				open={editState.editDialogOpen}
				item={editState.editItemData}
				onClose={handleEditDialogClose}
				onSave={handleEditSave}
			/>
			{selectedAuditItem && (
				<ItemAuditLogDialog
					open={isAuditDialogOpen}
					onClose={closeAuditDialog}
					itemName={selectedAuditItem.itemName}
					itemCode={selectedAuditItem.itemCode}
				/>
			)}
		</>
	);
};
